import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `data-screen-label="Do not call"` — plan unit P14-04 (design §15.6.7, FR-APP-31).
 *
 * WHAT IS ON IT: the meta line, "Add a number" / "Import a list" / "Export", the standing note,
 * the four figures, the source filter and the search, the table (number, name and the words
 * they said; when; how; covers; "Hear it" or "Details"; Remove), the refusal-phrase card and the
 * public opt-out card.
 *
 * STATES: loading, error, empty (nobody on the list at all), filtered-empty (a filter or a
 * search matched nobody — a different sentence, with the way back), populated.
 *
 * FOUR THINGS THIS SCREEN IS CAREFUL ABOUT.
 *
 * **1. The list is the ACCOUNT's.** `do_not_call_entry` has no `workspace_id` (0001), so the
 * meta line names the account and says it applies to "every agent". A person looking at one
 * workspace must not come away thinking a suppression is local to it. (Design audit 2026-10-15: the
 * line is the prototype's verbatim — "<account> · applies to outbound calls from every agent" — so the
 * earlier ", in every workspace" suffix is gone.)
 *
 * **2. A member may remove any entry — including one WeTalk added.** "Operators may add but
 * never remove" binds the operator, not the account. The API answers an impersonated removal
 * with `403 impersonation_forbidden`; that refusal is printed, and the row stays because the
 * list is re-read rather than edited in place.
 *
 * **3. An unreadable list is not an empty list.** A failed read renders a refusal and no
 * table, never "Nobody is on your list yet" — the second reads as "safe to dial everyone".
 *
 * **4. The filter and the search live in the URL.** `router.tsx` owns them; this screen only
 * asks for changes through `onSearch`.
 *
 * AMENDED 2026-09-23 (design fidelity, ADR 0018): the row's two actions are the prototype's two
 * text links ("Hear it" / "Details", then "Remove") in the right-aligned last cell, not buttons.
 * Remove still removes (the link's click is the action, its default navigation is suppressed)
 * and still names the person in its accessible name; "Details" still opens the row's detail.
 * A page opened as `/do-not-call#import` (Compliance's "Import a suppression list") opens with
 * the import panel already open.
 *
 * "HEAR IT" JUMPS TO THE AGENT'S HISTORY, as the prototype draws it (AMENDED 2026-09-23, register
 * 0z.1). The row carries the conversation AND the agent that heard it
 * (`conversationVoiceAgentId`), so "Hear it" is a link to
 * `/agents/$voiceAgentId?tab=history&conversation=…` — the only route that plays a recording.
 * History's range is "all": a suppression never expires, so the conversation may be old. A heard
 * refusal whose conversation is no longer on file keeps a "Hear it" button that says so, rather
 * than a link to nothing.
 *
 * AMENDED 2026-09-27 (customer-portal review #243–#260, identified by Konstantina):
 *   - The filters and the search are never unmounted (#243, #251). The first page is a
 *     `useQuery` with `keepPreviousData`, so a keystroke keeps the rows on screen until the new
 *     ones land; inside `useQueries` that option does nothing (each new key is a new observer with
 *     no previous data), which is why every keystroke used to swap the whole list area for
 *     "Reading…" and drop the search box with the focus in it. A refused read keeps the controls
 *     too, so a search can be corrected where it was typed; the box stops at the API's 120.
 *   - After an add, a removal or a refused removal the list is re-read, and the page says what
 *     happened and that it is updating (#249, #250).
 *   - Several numbers can be selected and removed together, after one confirmation (#254).
 *   - A row shows the note given with "Add a number" and the opt-out page's message where a
 *     call's words go, as the prototype draws a note (#245, #246), and its "How" tag says "Added by
 *     you" only when it was you (#256).
 *   - An empty list reads as empty whatever filter is on (#260).
 */
import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Button,
  Dialog,
  Icon,
  Input,
  Mono,
  Segmented,
  Table,
  TableBody,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
} from "@wetalk/ui";
import { Link, useRouterState } from "@tanstack/react-router";
import { Fragment, useState, type ReactNode } from "react";

import { DoNotCallAddPanel } from "./do-not-call-add-panel.js";
import { OptOutPageCard, RefusalPhraseCard } from "./do-not-call-cards.js";
import {
  DO_NOT_CALL_FILTER_OPTION,
  SEARCH_MAX,
  addedText,
  countText,
  refusalText,
  removedText,
  shownText,
  sourceTone,
} from "./do-not-call-copy.js";
import { DoNotCallImportPanel } from "./do-not-call-import-panel.js";
import { GLYPH } from "../../shell/glyph.js";
import { useGateway } from "../../api/provider.js";
import { QUERY_KEY, authSessionQuery, doNotCallQuery, optOutPageQuery } from "../../api/query.js";
import {
  DO_NOT_CALL_STANDING_NOTE,
  doNotCallEvidenceLabel,
  doNotCallSourceLabel,
} from "../../api/suppression-view.js";
import type {
  DoNotCallFilterView,
  DoNotCallRemovalView,
  DoNotCallRowView,
  ExportArtefactView,
} from "../../api/gateway.js";

import "./do-not-call.css";

export interface DoNotCallScreenProps {
  readonly filter: DoNotCallFilterView;
  readonly search: string;
  onSearch(next: Partial<{ readonly filter: DoNotCallFilterView; readonly q: string }>): void;
}

type OpenPanel = "add" | "import" | null;

export function DoNotCallScreen(props: DoNotCallScreenProps): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const hash = useRouterState({ select: (state) => state.location.hash });
  // Compliance's "Import a suppression list" lands here as `#import`: importing has one home.
  const [openPanel, setOpenPanel] = useState<OpenPanel>(() =>
    hash === "import" ? "import" : null,
  );
  const [openDetailId, setOpenDetailId] = useState<string | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  /** What the last add or removal did, said until the next one (review #249). */
  const [notice, setNotice] = usePortalMessage();

  const authSession = useQuery(authSessionQuery(gateway));
  const signedInPersonId = authSession.data?.person.personId ?? null;

  // "Show more" pages, remembered per filter-and-search so a new search starts at page one.
  const trailKey = `${props.filter}\u0000${props.search}`;
  const [trail, setTrail] = useState<{ readonly key: string; readonly cursor: readonly string[] }>(
    { key: trailKey, cursor: [] },
  );
  const moreCursor: readonly string[] = trail.key === trailKey ? trail.cursor : [];

  // Review #243: a `useQuery`, so `keepPreviousData` keeps this page's rows (and the controls
  // drawn beside them) while the next filter's or search's rows are read.
  const firstPage = useQuery({
    ...doNotCallQuery(gateway, props.filter, props.search, null),
    placeholderData: keepPreviousData,
  });
  const morePage = useQueries({
    queries: moreCursor.map((one) => doNotCallQuery(gateway, props.filter, props.search, one)),
  });
  const pageQuery = [firstPage, ...morePage];
  const shownRow = pageQuery.flatMap((one) => one.data?.row ?? []);
  const selectedShown = shownRow.filter((one) => selected.has(one.doNotCallEntryId));
  const optOutPage = useQuery(optOutPageQuery(gateway));

  const reread = (): void => {
    void queryClient.invalidateQueries({ queryKey: QUERY_KEY.doNotCall });
  };

  const remove = useMutation({
    mutationFn: (row: DoNotCallRowView) => gateway.removeDoNotCallEntry(row.doNotCallEntryId),
    onSuccess: (_nothing, row) => {
      setNotice(() => (portalText("finance", "m1035", { value1: row.e164 })));
    },
    // Review #250: re-read on a refusal too — "no longer on the list" must not leave the row there.
    onSettled: reread,
  });

  const removeSeveral = useMutation({
    mutationFn: (entryId: readonly string[]): Promise<DoNotCallRemovalView> =>
      gateway.removeDoNotCallEntries(entryId),
    onSuccess: (result) => {
      setNotice(() => (removedText(result.removedCount, result.notFoundCount)));
      setSelected(new Set());
    },
    onSettled: reread,
  });

  const exporting = useMutation({
    mutationFn: (): Promise<ExportArtefactView> => gateway.exportDoNotCallEntriesCsv(),
  });

  const meta =
    optOutPage.data === undefined
      ? portalText("finance", "m1036")
      : portalText("finance", "m1037", { value1: optOutPage.data.displayName });

  const toggle = (entryId: string): void => {
    setSelected((was) => {
      const next = new Set(was);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });
  };
  const allShownSelected = shownRow.length > 0 && selectedShown.length === shownRow.length;

  const page = firstPage.data;
  const listIsEmpty = page?.stat.find((one) => one.code === "on_the_list")?.value === 0;

  return (
    <section className="c-page dnc-page">
      <div className="c-headline-row dnc-headline">
        <div>
          <p className="dnc-kicker">{meta}</p>
          <h1 className="dnc-title">{portalText("finance", "m0912")}</h1>
        </div>
        <div className="dnc-actions">
          <Button
            variant="primary"
            aria-expanded={openPanel === "add"}
            onClick={() => {
              setNotice(null);
              setOpenPanel((open) => (open === "add" ? null : "add"));
            }}
          >
            <Icon glyph={GLYPH.plus} size="md" />{portalText("finance", "m1038")}</Button>
          <Button
            variant="secondary"
            aria-expanded={openPanel === "import"}
            onClick={() => {
              setNotice(null);
              setOpenPanel((open) => (open === "import" ? null : "import"));
            }}
          >{portalText("finance", "m1039")}</Button>
          <Button
            variant="secondary"
            disabled={exporting.isPending}
            onClick={() => {
              exporting.mutate();
            }}
          >
            <Icon glyph={GLYPH.downloadSimple} size="md" />
            {exporting.isPending ? portalText("finance", "m1040") : portalText("finance", "m0921")}
          </Button>
        </div>
      </div>

      <p className="dnc-lede">{DO_NOT_CALL_STANDING_NOTE()}</p>

      {exporting.isError ? (
        <p className="c-note c-note-critical" role="alert">
          {refusalText("export", exporting.error)}
        </p>
      ) : null}
      {exporting.data === undefined ? null : (
        <p className="c-note" role="status">
          <a href={exporting.data.downloadUrl} download>{portalText("finance", "m1041")}{exporting.data.fileExtension}
          </a>{" "}
          {/* Rows, not numbers: a number on the list for one agent and for every agent is two
              entries, so two rows of the file (review #247). */}
          · {countText(exporting.data.rowCount)}{" "}
          {exporting.data.rowCount === 1 ? "row" : "rows"}{portalText("finance", "m1042")}{" "}
          {exporting.data.expiresAtText}.
        </p>
      )}

      {openPanel === "add" ? (
        <DoNotCallAddPanel
          onClose={() => {
            setOpenPanel(null);
          }}
          onAdded={(row) => {
            setNotice(() => (addedText(row)));
          }}
        />
      ) : null}
      {openPanel === "import" ? (
        <DoNotCallImportPanel
          onClose={() => {
            setOpenPanel(null);
          }}
        />
      ) : null}

      {notice === null ? null : (
        <p className="c-note dnc-notice" role="status">
          {notice}
        </p>
      )}

      {page === undefined ? null : (
        <ul className="dnc-stats">
          {page.stat.map((one) => (
            <li key={one.code} className="dnc-stat">
              <span className="dnc-stat-label">{one.label}</span>
              <span className="dnc-stat-value">{countText(one.value)}</span>
              <span className="dnc-stat-note">{one.note}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="dnc-controls">
        <Segmented
          name="doNotCallFilter"
          legend={portalText("finance", "m1190")}
          value={props.filter}
          options={DO_NOT_CALL_FILTER_OPTION}
          onChange={(next) => {
            props.onSearch({ filter: next });
          }}
        />
        <span className="dnc-search">
          <span className="dnc-search-glyph">
            <Icon glyph={GLYPH.magnifyingGlass} size="sm" />
          </span>
          <Input
            type="search"
            aria-label={portalText("finance", "m1043")}
            placeholder={portalText("finance", "m1044")}
            maxLength={SEARCH_MAX}
            value={props.search}
            onChange={(event) => {
              props.onSearch({ q: event.currentTarget.value });
            }}
          />
        </span>
        <span className="dnc-shown">
          {page === undefined
            ? null
            : shownText(
                pageQuery.reduce((sum, one) => sum + (one.data?.row.length ?? 0), 0),
                page.matchedCount,
              )}
          {firstPage.isFetching && page !== undefined ? (
            <span className="dnc-updating" role="status">
              {" "}{portalText("finance", "m1045")}</span>
          ) : null}
        </span>
      </div>

      {remove.isError ? (
        <p className="c-note c-note-critical" role="alert">
          {refusalText("remove", remove.error)}
        </p>
      ) : null}
      {removeSeveral.isError ? (
        <p className="c-note c-note-critical" role="alert">
          {refusalText("remove", removeSeveral.error)}
        </p>
      ) : null}

      {firstPage.isError ? (
        <p className="c-note c-note-critical" role="alert">{portalText("finance", "m1046")}{firstPage.error.message}
        </p>
      ) : page === undefined ? (
        <p className="c-note">{portalText("finance", "m1047")}</p>
      ) : page.row.length === 0 ? (
        listIsEmpty || (props.filter === "all" && props.search.trim() === "") ? (
          <div className="c-panel dnc-empty">
            <p className="c-empty-title">{portalText("finance", "m1048")}</p>
            <p className="c-empty-dek">{portalText("finance", "m1049")}</p>
          </div>
        ) : (
          <div className="c-panel dnc-empty">
            <p className="c-empty-title">{portalText("finance", "m1050")}</p>
            <p className="c-empty-dek">{portalText("finance", "m1051")}{props.search.trim() === "" ? "" : portalText("finance", "m1052", { value1: props.search.trim() })}{portalText("finance", "m1053")}</p>
            <div className="c-row dnc-more">
              <Button
                variant="secondary"
                onClick={() => {
                  props.onSearch({ filter: "all", q: "" });
                }}
              >{portalText("finance", "m1054")}</Button>
            </div>
          </div>
        )
      ) : (
        <>
          {selectedShown.length === 0 ? null : (
            <div className="c-row dnc-selection" role="region" aria-label={portalText("finance", "m1055")}>
              <span className="dnc-selection-count">
                {countText(selectedShown.length)}{portalText("finance", "m1056")}</span>
              <Button
                variant="secondary"
                disabled={removeSeveral.isPending}
                onClick={() => {
                  setConfirmingRemoval(true);
                }}
              >
                {removeSeveral.isPending ? portalText("finance", "m1057") : portalText("finance", "m1058")}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setSelected(new Set());
                }}
              >{portalText("finance", "m1059")}</Button>
            </div>
          )}
          <div className="dnc-table">
            <Table caption={portalText("finance", "m1060")}>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>
                    <input
                      type="checkbox"
                      className="dnc-check"
                      aria-label={portalText("finance", "m1061")}
                      checked={allShownSelected}
                      onChange={() => {
                        setSelected(
                          allShownSelected
                            ? new Set()
                            : new Set(shownRow.map((one) => one.doNotCallEntryId)),
                        );
                      }}
                    />
                  </TableHeaderCell>
                  <TableHeaderCell>{portalText("finance", "m1062")}</TableHeaderCell>
                  <TableHeaderCell>{portalText("finance", "m0736")}</TableHeaderCell>
                  <TableHeaderCell>{portalText("finance", "m1063")}</TableHeaderCell>
                  <TableHeaderCell>{portalText("finance", "m0974")}</TableHeaderCell>
                  <TableHeaderCell>
                    <span className="c-visually-hidden">{portalText("finance", "m0445")}</span>
                  </TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shownRow.map((row) => (
                  <EntryRow
                    key={row.doNotCallEntryId}
                    row={row}
                    howText={doNotCallSourceLabel(row, signedInPersonId)}
                    selected={selected.has(row.doNotCallEntryId)}
                    detailOpen={openDetailId === row.doNotCallEntryId}
                    removing={remove.isPending && remove.variables?.doNotCallEntryId === row.doNotCallEntryId}
                    onSelect={() => {
                      toggle(row.doNotCallEntryId);
                    }}
                    onDetail={() => {
                      setOpenDetailId((open) =>
                        open === row.doNotCallEntryId ? null : row.doNotCallEntryId,
                      );
                    }}
                    onRemove={() => {
                      remove.mutate(row);
                    }}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {page === undefined ? null : (
        <MorePages
          last={pageQuery[pageQuery.length - 1]}
          onMore={(next) => {
            setTrail({ key: trailKey, cursor: [...moreCursor, next] });
          }}
        />
      )}

      {confirmingRemoval ? (
        <Dialog
          open
          title={portalText("finance", "dnc.removeSelected", { count: selectedShown.length, value1: countText(selectedShown.length) })}
          onClose={() => {
            setConfirmingRemoval(false);
          }}
          actions={
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  setConfirmingRemoval(false);
                }}
              >
                {selectedShown.length === 1 ? portalText("finance", "m1065") : portalText("finance", "m1066")}
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setConfirmingRemoval(false);
                  removeSeveral.mutate(selectedShown.map((one) => one.doNotCallEntryId));
                }}
              >{portalText("finance", "m0239")}{countText(selectedShown.length)}
              </Button>
            </>
          }
        >
          <p>{portalText("finance", "m1067")}{" "}
            {selectedShown.length === 1 ? portalText("finance", "m1068") : portalText("finance", "m1069")}{portalText("finance", "m1070")}</p>
        </Dialog>
      ) : null}

      <div className="dnc-cards">
        <RefusalPhraseCard />
        <OptOutPageCard />
      </div>
    </section>
  );
}

interface EntryRowProps {
  readonly row: DoNotCallRowView;
  /** The "How" tag — "Added by you" only when it was you (review #256). */
  readonly howText: string;
  readonly selected: boolean;
  readonly detailOpen: boolean;
  readonly removing: boolean;
  readonly onSelect: () => void;
  readonly onDetail: () => void;
  readonly onRemove: () => void;
}

function EntryRow({
  row,
  howText,
  selected,
  detailOpen,
  removing,
  onSelect,
  onDetail,
  onRemove,
}: EntryRowProps): ReactNode {
  usePortalTranslation("finance");
  const who = row.displayName ?? row.e164;
  return (
    <Fragment>
      <tr>
        <td className="dnc-check-cell">
          <input
            type="checkbox"
            className="dnc-check"
            aria-label={portalText("finance", "m1071", { value1: who })}
            checked={selected}
            onChange={onSelect}
          />
        </td>
        <td className="dnc-entry">
          <span className="dnc-number">{row.e164}</span>
          {row.displayName === null ? null : <span className="dnc-name">{row.displayName}</span>}
          <EntryWords row={row} />
        </td>
        <td className="dnc-muted">{row.whenText}</td>
        <td className="dnc-how">
          <Tag tone={sourceTone(row.source)}>{howText}</Tag>
        </td>
        <td className="dnc-muted">{row.scopeText}</td>
        <td className="dnc-actions-cell">
          <span className="dnc-row-actions">
            {row.conversationId !== null && row.conversationVoiceAgentId !== null ? (
              <Link
                to="/agents/$voiceAgentId"
                params={{ voiceAgentId: row.conversationVoiceAgentId }}
                search={{
                  tab: "history",
                  range: "all",
                  outcome: "all",
                  q: "",
                  conversation: row.conversationId,
                  analyticsRange: "14d",
                  agentVersion: "",
                  concern: "gaps",
                }}
                aria-label={`${doNotCallEvidenceLabel(row.source)} — ${who}`}
              >
                {doNotCallEvidenceLabel(row.source)}
              </Link>
            ) : (
              <a
                href="#"
                aria-expanded={detailOpen}
                aria-label={`${doNotCallEvidenceLabel(row.source)} — ${who}`}
                onClick={(event) => {
                  event.preventDefault();
                  onDetail();
                }}
              >
                {doNotCallEvidenceLabel(row.source)}
              </a>
            )}
            <a
              href="#"
              aria-disabled={removing}
              aria-label={portalText("finance", "m1072", { value1: who })}
              onClick={(event) => {
                event.preventDefault();
                if (!removing) onRemove();
              }}
            >
              {removing ? portalText("finance", "m1057") : portalText("finance", "m1073")}
            </a>
          </span>
        </td>
      </tr>
      {detailOpen ? (
        <tr>
          <td colSpan={6} className="dnc-detail">
            <EntryDetail row={row} howText={howText} />
          </td>
        </tr>
      ) : null}
    </Fragment>
  );
}

/**
 * Under the number: what they said on the call, what they wrote on the opt-out page, or the note
 * given when it was added — the prototype's one line of words under a row (review #245, #246). A
 * person's own words are quoted; a note is not, because nobody said it.
 */
function EntryWords({ row }: { readonly row: DoNotCallRowView }): ReactNode {
  usePortalTranslation("finance");
  if (row.spokenWords !== null) return <span className="dnc-words">“{row.spokenWords}”</span>;
  if (row.optOutMessage !== null) return <span className="dnc-words">“{row.optOutMessage}”</span>;
  if (row.note !== null) return <span className="dnc-words">{row.note}</span>;
  return null;
}

/** What is known about how a number got onto the list, per source. */
function EntryDetail({ row, howText }: { readonly row: DoNotCallRowView; readonly howText: string }): ReactNode {
  usePortalTranslation("finance");
  const note = row.note === null ? null : <p>{portalText("finance", "m1074")}{row.note}</p>;
  const message =
    row.optOutMessage === null ? null : (
      <p>
        {row.source === "opt_out_page" ? portalText("finance", "m1075") : portalText("finance", "m1076")}: “
        {row.optOutMessage}”
      </p>
    );
  switch (row.source) {
    case "said_on_call":
      return (
        <>
          <p>
            {row.conversationId === null ? (
              portalText("finance", "m1077")
            ) : (
              <>{portalText("finance", "m1078")}<Mono>{row.conversationId}</Mono>{portalText("finance", "m1079")}</>
            )}
          </p>
          {message}
        </>
      );
    case "opt_out_page":
      return (
        <>
          <p>{portalText("finance", "m1080")}</p>
          {message}
        </>
      );
    case "added_by_operator":
      return (
        <>
          <p>{portalText("finance", "m1081")}{row.addedByOperatorName ?? portalText("finance", "m1082")}{portalText("finance", "m1083")}</p>
          {note}
          {message}
        </>
      );
    case "added_by_member":
      return (
        <>
          <p>{howText}.</p>
          {note}
          {message}
        </>
      );
  }
}

interface PageState {
  readonly data?: { readonly nextCursor: string | null } | undefined;
  readonly error: Error | null;
}

/** "Show more" under the table, or why the next page is not there. */
function MorePages({
  last,
  onMore,
}: {
  readonly last: PageState | undefined;
  readonly onMore: (nextCursor: string) => void;
}): ReactNode {
  usePortalTranslation("finance");
  if (last === undefined) return null;
  if (last.error !== null) {
    return (
      <p className="c-note c-note-critical" role="alert">{portalText("finance", "m1084")}{last.error.message}
      </p>
    );
  }
  if (last.data === undefined) return <p className="c-note">{portalText("finance", "m1085")}</p>;
  const next = last.data.nextCursor;
  if (next === null) return null;
  return (
    <div className="c-row dnc-more">
      <Button
        variant="secondary"
        onClick={() => {
          onMore(next);
        }}
      >{portalText("finance", "m1086")}</Button>
    </div>
  );
}
